<template>
  <div class="supplier-layout" :class="{ 'is-mobile': isMobile }">
    <!-- 一级菜单栏 (80px) -->
    <aside class="layout-columns-aside" :class="{ 'is-collapse': isCollapse }">
      <div class="columns-logo" @click="$router.push('/dashboard')" title="安徽亿佳供应链 · 管理端">
        <img class="columns-logo-img" src="/logo/favicon.png" alt="亿佳">
      </div>
      <ul class="columns-menu-list">
        <li
          v-for="(item, index) in menuList"
          :key="item.path"
          class="columns-menu-item"
          :class="{ 'is-active': activeMenuIndex === index }"
          :title="item.title"
          @click="handleMenuClick(item, index)"
        >
          <i :class="'el-icon-' + item.icon"></i>
          <span class="item-text">{{ abbreviate(item.title) }}</span>
        </li>
      </ul>
    </aside>

    <!-- 右侧区域（二级菜单 + 主内容） -->
    <div class="layout-right">
      <!-- 二级菜单栏 -->
      <transition name="menu-slide">
        <aside
          v-if="!isCollapse && childMenuList.length > 0"
          class="layout-sub-aside"
        >
          <div class="sub-aside-header">{{ currentMenuTitle }}</div>
          <el-scrollbar class="sub-aside-scrollbar">
            <ul class="sub-menu-list">
              <li
                v-for="child in childMenuList"
                :key="child.path"
                class="sub-menu-item"
                :class="{ 'is-active': isActiveChild(child.path) }"
                @click="handleSubMenuClick(child)"
              >
                <span>{{ child.title }}</span>
              </li>
            </ul>
          </el-scrollbar>
        </aside>
      </transition>

      <!-- 主内容区域 -->
      <div class="layout-main-wrapper">
        <!-- 顶部导航栏 -->
        <header class="layout-header">
          <div class="header-left">
            <i
              class="header-collapse-btn el-icon-s-fold"
              :class="{ 'is-active': !isCollapse }"
              @click="toggleCollapse"
              v-if="childMenuList.length > 1"
            ></i>
            <breadcrumb />
          </div>
          <div class="header-right">
            <el-badge :value="unreadCount" :max="99" :hidden="unreadCount === 0" class="header-action" @click.native="$router.push('/system/message')">
              <i class="el-icon-bell"></i>
            </el-badge>
            <el-dropdown trigger="click" @command="handleCommand">
              <span class="user-info">
                <avatar />
                <span class="user-name">{{ userInfo.name }}<em class="user-role" v-if="roleName">{{ roleName }}</em></span>
                <i class="el-icon-arrow-down"></i>
              </span>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item command="profile">个人中心</el-dropdown-item>
                <el-dropdown-item command="password">修改密码</el-dropdown-item>
                <el-dropdown-item divided command="logout">退出登录</el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </div>
        </header>

        <!-- 顶部 TagsView -->
        <tags-view />

        <!-- 内容区 -->
        <main class="layout-main">
          <router-view />
        </main>
      </div>

      <!-- 个人中心（仅本人基本信息的自改入口） -->
      <el-dialog title="个人中心" :visible.sync="profile.visible" width="440px" append-to-body :close-on-click-modal="false">
        <el-form :model="profile.form" label-width="90px" size="medium">
          <el-form-item label="登录账号"><el-input :value="profile.form.account" disabled /></el-form-item>
          <el-form-item label="姓名"><el-input v-model="profile.form.name" /></el-form-item>
          <el-form-item label="手机号"><el-input v-model="profile.form.phone" /></el-form-item>
          <el-form-item label="邮箱"><el-input v-model="profile.form.email" /></el-form-item>
        </el-form>
        <div slot="footer">
          <el-button @click="profile.visible = false">取消</el-button>
          <el-button type="primary" @click="saveProfile">保存</el-button>
        </div>
      </el-dialog>

      <!-- 修改密码 -->
      <el-dialog title="修改密码" :visible.sync="pwd.visible" width="440px" append-to-body :close-on-click-modal="false">
        <el-form ref="pwdForm" :model="pwd.form" :rules="pwd.rules" label-width="90px" size="medium">
          <el-form-item label="原密码" prop="old">
            <el-input v-model="pwd.form.old" type="password" show-password placeholder="请输入原密码" />
          </el-form-item>
          <el-form-item label="新密码" prop="password">
            <el-input v-model="pwd.form.password" type="password" show-password placeholder="请输入新密码" />
          </el-form-item>
          <el-form-item label="确认密码" prop="confirm">
            <el-input v-model="pwd.form.confirm" type="password" show-password placeholder="请再次输入" />
          </el-form-item>
        </el-form>
        <div slot="footer">
          <el-button @click="pwd.visible = false">取消</el-button>
          <el-button type="primary" @click="savePwd">确定</el-button>
        </div>
      </el-dialog>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex'
import Breadcrumb from './components/Breadcrumb.vue'
import Avatar from './components/Avatar.vue'
import TagsView from './components/TagsView.vue'

export default {
  name: 'ManagerLayout',
  components: { Breadcrumb, Avatar, TagsView },
  data() {
    return {
      isMobile: false,
      profile: { visible: false, form: { account: '', name: '', phone: '', email: '' } },
      pwd: {
        visible: false,
        form: { old: '', password: '', confirm: '' },
        rules: {
          old: [{ required: true, message: '请输入原密码', trigger: 'blur' }],
          password: [{ required: true, message: '请输入新密码', trigger: 'blur' }],
          confirm: [
            { required: true, message: '请再次输入', trigger: 'blur' },
            { validator: (r, v, cb) => v !== this.pwd.form.password ? cb(new Error('两次输入不一致')) : cb(), trigger: 'blur' },
          ],
        },
      },
    }
  },
  computed: {
    ...mapState(['menuList', 'activeMenuIndex', 'childMenuList', 'isCollapse', 'userInfo', 'messages', 'accounts']),
    ...mapGetters(['currentMenuTitle']),
    roleName() {
      return this.userInfo.roleName || ''
    },
    unreadCount() {
      return (this.messages || []).filter(m => !m.read).length
    },
  },
  watch: {
    $route: {
      immediate: true,
      handler(to) {
        this.$store.dispatch('syncMenuFromPath', to.path)
      },
    },
  },
  created() {
    this.checkMobile()
    window.addEventListener('resize', this.checkMobile)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkMobile)
  },
  methods: {
    ...mapMutations(['SET_ACTIVE_MENU_INDEX', 'TOGGLE_COLLAPSE']),

    /** 一级菜单点击 */
    handleMenuClick(item, index) {
      if (this.activeMenuIndex === index) return
      this.SET_ACTIVE_MENU_INDEX(index)
      // 跳转到该一级菜单的第一个子页面或自身
      const children = item.children
      if (children && children.length > 0) {
        this.$router.push(children[0].path).catch(() => {})
      } else {
        this.$router.push(item.path).catch(() => {})
      }
    },

    /** 二级菜单点击 */
    handleSubMenuClick(child) {
      this.$router.push(child.path).catch(() => {})
    },

    /** 判断子菜单是否当前激活 */
    isActiveChild(path) {
      return this.$route.path === path
    },

    /** 缩短标题文字 */
    abbreviate(title) {
      if (!title) return ''
      return title.length > 4 ? title.substring(0, 4) : title
    },

    /** 切换二级菜单折叠 */
    toggleCollapse() {
      this.TOGGLE_COLLAPSE()
    },

    /** 个人中心：打开并加载当前账号可自改的基本信息 */
    openProfile() {
      const acc = this.accounts.find(a => a.id === this.userInfo.id)
      if (!acc) { this.$message.error('未找到当前账号'); return }
      this.profile.form = { account: acc.account, name: acc.name, phone: acc.phone, email: acc.email }
      this.profile.visible = true
    },
    saveProfile() {
      const acc = this.accounts.find(a => a.id === this.userInfo.id)
      if (!acc) return
      const payload = { id: acc.id, name: this.profile.form.name, phone: this.profile.form.phone, email: this.profile.form.email }
      this.$store.commit('UPSERT_ACCOUNT', payload)
      // 同步 userInfo（菜单/角色不变，仅基本信息）
      this.$store.commit('SET_USER_INFO', { ...this.userInfo, name: this.profile.form.name, phone: this.profile.form.phone, email: this.profile.form.email })
      try { window.sessionStorage.setItem('manager_user', JSON.stringify(this.$store.state.userInfo)) } catch (e) {}
      this.$store.dispatch('addLog', { module: '系统', action: '修改个人信息', target: this.profile.form.name, detail: '个人中心自改' })
      this.profile.visible = false
      this.$message.success('已保存')
    },
    savePwd() {
      this.$refs.pwdForm.validate(valid => {
        if (!valid) return
        const acc = this.accounts.find(a => a.id === this.userInfo.id)
        if (!acc) return
        if (acc.password !== this.pwd.form.old) { this.$message.error('原密码错误'); return }
        this.$store.commit('RESET_ACCOUNT_PASSWORD', { id: acc.id, password: this.pwd.form.password })
        this.$store.dispatch('addLog', { module: '系统', action: '修改密码', target: this.userInfo.name, detail: '个人中心修改密码' })
        this.pwd.visible = false
        this.$message.success('密码已修改')
      })
    },

    /** 检测移动端 */
    checkMobile() {
      this.isMobile = document.body.clientWidth < 1000
    },

    /** 头部下拉操作 */
    handleCommand(command) {
      switch (command) {
        case 'profile':
          this.openProfile()
          break
        case 'password':
          this.pwd.visible = true
          this.pwd.form = { old: '', password: '', confirm: '' }
          this.$nextTick(() => { if (this.$refs.pwdForm) this.$refs.pwdForm.clearValidate() })
          break
        case 'logout':
          this.$confirm('确认退出登录?', '提示', {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning',
          }).then(() => {
            this.$store.dispatch('logout').then(() => {
              this.$router.replace('/login').catch(() => {})
            })
          }).catch(() => {})
          break
      }
    },
  },
}
</script>

<style lang="scss" scoped>
/* ========== CSS 变量 =========== */
$primary-color: #2C68FF;
$primary-hover: #548AFF;
$columns-width: 80px;
$sub-aside-width: 200px;
$header-height: 50px;

$columns-bg: #001529;
$columns-color: rgba(255, 255, 255, 0.65);
$columns-active-bg: $primary-color;
$columns-active-color: #fff;
$sub-aside-bg: #fff;
$header-bg: #fff;
$main-bg: #f5f5f5;

/* ========== 根容器 =========== */
.supplier-layout {
  display: flex;
  height: 100vh;
  width: 100%;
  overflow: hidden;
}

/* ========== 一级菜单栏 (80px) =========== */
.layout-columns-aside {
  width: $columns-width;
  min-width: $columns-width;
  height: 100%;
  background: $columns-bg;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  transition: all 0.28s;
  z-index: 100;
  box-shadow: 2px 0 6px rgba(0, 21, 41, 0.08);

  &.is-collapse {
    // 收起状态（保留宽度但可做样式变化）
  }
}

.columns-logo {
  height: $header-height;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: $primary-hover;
  font-size: 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);

  &:hover {
    background: rgba(255, 255, 255, 0.04);
  }
}

.columns-logo-img {
  width: 36px;
  height: 36px;
  object-fit: contain;
  border-radius: 4px;
  background: #fff;
  padding: 2px;
}

.columns-menu-list {
  list-style: none;
  padding: 8px 0;
  margin: 0;
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.15);
    border-radius: 2px;
  }
}

.columns-menu-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 56px;
  cursor: pointer;
  color: $columns-color;
  transition: all 0.25s ease;
  position: relative;
  padding: 4px 0;

  i {
    font-size: 20px;
    margin-bottom: 3px;
  }

  .item-text {
    font-size: 12px;
    line-height: 1.2;
    text-align: center;
    max-width: 68px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &:hover {
    color: #fff;
    background: rgba(255, 255, 255, 0.06);
  }

  &.is-active {
    color: $columns-active-color;
    background: $columns-active-bg;

    &::before {
      content: '';
      position: absolute;
      right: 0;
      top: 6px;
      bottom: 6px;
      width: 3px;
      background: #fff;
      border-radius: 2px 0 0 2px;
    }
  }
}

/* ========== 右侧区域 =========== */
.layout-right {
  display: flex;
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

/* ========== 二级菜单栏 =========== */
.layout-sub-aside {
  width: $sub-aside-width;
  min-width: $sub-aside-width;
  height: 100%;
  background: $sub-aside-bg;
  border-right: 1px solid #e8e8e8;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  z-index: 90;
}

.sub-aside-header {
  height: $header-height;
  line-height: $header-height;
  padding: 0 16px;
  font-size: 15px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.85);
  border-bottom: 1px solid #f0f0f0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sub-aside-scrollbar {
  flex: 1;
  overflow: hidden;
}

.sub-menu-list {
  list-style: none;
  padding: 8px 0;
  margin: 0;
}

.sub-menu-item {
  height: 42px;
  line-height: 42px;
  padding: 0 20px;
  cursor: pointer;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.65);
  transition: all 0.2s;
  position: relative;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:hover {
    color: $primary-color;
    background: #e6f4ff;
  }

  &.is-active {
    color: $primary-color;
    background: #e6f4ff;
    font-weight: 500;

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 8px;
      bottom: 8px;
      width: 3px;
      background: $primary-color;
      border-radius: 0 2px 2px 0;
    }
  }
}

/* ========== 主内容区 =========== */
.layout-main-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
  background: $main-bg;
}

/* ========== 顶部 Header =========== */
.layout-header {
  height: $header-height;
  min-height: $header-height;
  background: $header-bg;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  box-shadow: 0 1px 4px rgba(0, 21, 41, 0.06);
  flex-shrink: 0;
  z-index: 80;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-collapse-btn {
  font-size: 20px;
  color: rgba(0, 0, 0, 0.45);
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.25s;

  &:hover {
    color: rgba(0, 0, 0, 0.75);
    background: #f0f0f0;
  }

  &.is-active {
    transform: rotate(180deg);
  }
}

.header-right {
  display: flex;
  align-items: center;
  gap: 18px;
}

.header-action {
  cursor: pointer;
  font-size: 18px;
  color: rgba(0, 0, 0, 0.45);

  &:hover {
    color: $primary-color;
  }
}

.user-info {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  color: rgba(0, 0, 0, 0.85);
  font-size: 14px;

  .user-name {
    max-width: 100px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    .user-role {
      display: inline-block;
      margin-left: 6px;
      padding: 0 6px;
      font-size: 11px;
      font-style: normal;
      line-height: 16px;
      color: #2C68FF;
      background: #e6f4ff;
      border-radius: 8px;
      vertical-align: middle;
    }
  }

  .el-icon-arrow-down {
    font-size: 12px;
    color: rgba(0, 0, 0, 0.45);
  }
}

/* ========== Main 内容区 =========== */
.layout-main {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 16px 20px;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 3px;
  }
}

/* ========== 动画 =========== */
.menu-slide-enter-active,
.menu-slide-leave-active {
  transition: all 0.25s ease;
}
.menu-slide-enter,
.menu-slide-leave-to {
  margin-left: -$sub-aside-width;
  opacity: 0;
}

/* ========== 移动端适配 =========== */
.supplier-layout.is-mobile {
  .layout-columns-aside {
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    z-index: 200;
  }
  .layout-sub-aside {
    position: fixed;
    top: 0;
    left: $columns-width;
    bottom: 0;
    z-index: 199;
    box-shadow: 2px 0 8px rgba(0, 0, 0, 0.15);
  }
}
</style>
